'use strict';

import * as THREE from 'three';
import { settingsData, threeJsVariables, isMobile, projectMode, isTablet, globalTextures } from '../../settings';
import {lazyLoadingAll, getUrlParam} from '../../functions';
import {
    addObjectsToOpacity,
    bundle_initAutoBuildingsScript,
    initAutoBuildingsScript,
    loadBuildingModelsFromAzure,
    loadModelTexturesFromAzure,
} from './autoBuildingFunctions';
import {
    bundle_initAutoHighlightsScript,
    initAutoHighlightsScript,
    loadHighlightsModelFromAzure,
    setProjectSettings,
} from './autoSelectionBoxes';
import {getCrmData} from '../crm/crm-requests';
import {getPoiData} from '../crm/crm-requests';
import { initPoiData } from '../poi/poi';
import { environmentToggle, setHMap, basic_tile } from './environment';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

import { flyCameraAnimation } from './camera';
import {
    loadSixBoxImages, sphereBackground,
    sphereSceneBackground,
} from './sceneBackground';
import { removePreloader } from '../preloader/preloader';
import { loadLordIconsCollection } from '../../lordicon';
import { addSceneObjects, sceneObjectsGroup } from '../points/points';
import { loadCylinderGrid } from '../cylinderGrid/cylinderGrid';
import { Color, Group, LinearEncoding, FrontSide, DoubleSide,TextureLoader,MeshStandardMaterial,
         MeshBasicMaterial,EquirectangularReflectionMapping,RepeatWrapping, BufferGeometry,
         BufferAttribute, Mesh, MeshPhongMaterial, ShaderMaterial } from 'three';
import { Cylinder } from '../mode/cylinder';
import { FloorPlan } from '../mode/floorPlan';
import { LiteMode } from '../mode/liteMode';
import { BoxBufferGeometry } from 'three';
import { Disappear } from '../objectToDisappear/Disappear';


const multiTextures = {};

const v_normalize = (a) => {
    const d = Math.sqrt(a[0]*a[0]+a[1]*a[1]+a[2]*a[2]);
    a[0] = a[0]/d;
    a[1] = a[1]/d;
    a[2] = a[2]/d;
};

const backgroundShader = new ShaderMaterial({
    uniforms: {
        map1  : { value: null },
        map2  : { value: null },
        t     : { value: 0.0 },
        fogColor   : { value: new Color()  },
        fogDensity : { value: 0.0  },
    },
    vertexShader: [
        'varying vec2 lUv;',
        'varying float z;',
        'void main() {',
            'lUv = uv;',
            'gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);',
            'z = -gl_Position.z;',
        '}'].join(''),
    fragmentShader: [
        'uniform sampler2D map1;',
        'uniform sampler2D map2;',
        'uniform vec3 fogColor;',
        'uniform float fogDensity;',
        'uniform float t;',
        'varying float z;',
        'varying vec2 lUv;',
        'void main(){',
            'float fogDepth = z;',
            'float fogFactor = 1.0 - exp( -fogDensity * fogDensity * fogDepth * fogDepth );',
            'vec4 c1 = texture2D(map1, lUv);',
            'vec4 c1FogColor = vec4(mix( c1.rgb, fogColor, fogFactor ), 1.0);',
            'vec4 c2 = texture2D(map2, lUv);',
            'gl_FragColor = mix(c1FogColor,c2,t);',
        '}'].join(''),
    side        : FrontSide,
    depthTest   : false,
    depthWrite  : false,
    transparent : false,
});


const generateRegion = (vx,vy,vz) => {
    let w = 500;
    let hw = w/2;
    let a1 = [0,0,0];
    let a2 = [0,0,0];
    let a3 = [0,0,0];
    let a4 = [0,0,0];
    let v1 = [1,0,0];
    let v2 = [0,0,1];
    let v3 = [0,1,0];
    let b1 = [0,0,0];
    let b2 = [0,0,0];
    let b3 = [0,0,0];
    let b4 = [0,0,0];
    a1[0] = vz[0]*hw - vx[0]*hw + vy[0]*hw;
    a1[1] = vz[1]*hw - vx[1]*hw + vy[1]*hw;
    a1[2] = vz[2]*hw - vx[2]*hw + vy[2]*hw;
    a2[0] = vz[0]*hw + vx[0]*hw + vy[0]*hw;
    a2[1] = vz[1]*hw + vx[1]*hw + vy[1]*hw;
    a2[2] = vz[2]*hw + vx[2]*hw + vy[2]*hw;
    a3[0] = vz[0]*hw + vx[0]*hw - vy[0]*hw;
    a3[1] = vz[1]*hw + vx[1]*hw - vy[1]*hw;
    a3[2] = vz[2]*hw + vx[2]*hw - vy[2]*hw;
    a4[0] = vz[0]*hw - vx[0]*hw - vy[0]*hw;
    a4[1] = vz[1]*hw - vx[1]*hw - vy[1]*hw;
    a4[2] = vz[2]*hw - vx[2]*hw - vy[2]*hw;

    //  a1   a2
    //
    //  a4   a3
    //
    let count = 16;
    let delta = 1/count;
    v2[0] = vx[0]*w*delta;
    v2[1] = vx[1]*w*delta;
    v2[2] = vx[2]*w*delta;
    v3[0] = vy[0]*w*delta;
    v3[1] = vy[1]*w*delta;
    v3[2] = vy[2]*w*delta;

    let buffp = new Array();
    let buffu = new Array();
    let u1 = [0,0];
    let u2 = [0,0];
    let u3 = [0,0];
    let u4 = [0,0];
    for (let y=0;y<count;y++) {
        for (let x=0;x<count;x++) {

            b1[0] = a1[0] - v3[0]*y + v2[0]*x;
            b1[1] = a1[1] - v3[1]*y + v2[1]*x;
            b1[2] = a1[2] - v3[2]*y + v2[2]*x;
            b2[0] = b1[0] + v2[0];
            b2[1] = b1[1] + v2[1];
            b2[2] = b1[2] + v2[2];
            b3[0] = b1[0] - v3[0];
            b3[1] = b1[1] - v3[1];
            b3[2] = b1[2] - v3[2];
            b4[0] = b2[0] - v3[0];
            b4[1] = b2[1] - v3[1];
            b4[2] = b2[2] - v3[2];

            v_normalize(b1);
            v_normalize(b2);
            v_normalize(b3);
            v_normalize(b4);
            b1[0] = b1[0]*w;
            b1[1] = b1[1]*w;
            b1[2] = b1[2]*w;
            b2[0] = b2[0]*w;
            b2[1] = b2[1]*w;
            b2[2] = b2[2]*w;
            b3[0] = b3[0]*w;
            b3[1] = b3[1]*w;
            b3[2] = b3[2]*w;
            b4[0] = b4[0]*w;
            b4[1] = b4[1]*w;
            b4[2] = b4[2]*w;

            u1[0] = delta*x;
            u1[1] = 1.0 - delta*y;
            u2[0] = u1[0] + delta;
            u2[1] = u1[1];
            u3[0] = u1[0];
            u3[1] = u1[1] - delta;
            u4[0] = u1[0] + delta;
            u4[1] = u1[1] - delta;

            buffp.push(b1[0],b1[1],b1[2]);
            buffp.push(b2[0],b2[1],b2[2]);
            buffp.push(b3[0],b3[1],b3[2]);
            buffp.push(b3[0],b3[1],b3[2]);
            buffp.push(b2[0],b2[1],b2[2]);
            buffp.push(b4[0],b4[1],b4[2]);

            buffu.push(u1[0],u1[1]);
            buffu.push(u2[0],u2[1]);
            buffu.push(u3[0],u3[1]);
            buffu.push(u3[0],u3[1]);
            buffu.push(u2[0],u2[1]);
            buffu.push(u4[0],u4[1]);
        }
    }

    let geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(buffp),3));
    geometry.setAttribute('uv', new BufferAttribute(new Float32Array(buffu),2));
    let material = backgroundShader.clone();
    return new Mesh(geometry,material);
};

const prepapeSphere = (textureLoader) => {

    const {cdnRepository,projectFolder} = config;
    const {imageCdnQualityUrl,staticFolder,cylinderBg} = config.resources.background;

    const imgUrl1 = `${cdnRepository}/${isMobile?imageCdnQualityUrl.mobile:imageCdnQualityUrl.desktop}/${projectFolder}/resources/textures/background`
    const imgUrl2 = '/assets/model/cylinder';

    let group = new Group();
    group.name = 'Background';

    const format = '.jpg';
    const format2 = '.jpg';
    let m1 = generateRegion([0,0,-1],[0,1,0],[ 1,0,0]);
    let m2 = generateRegion([0,0, 1],[0,1,0],[-1,0,0]);
    let m3 = generateRegion([ 1,0,0],[0,1,0],[0,0, 1]);
    let m4 = generateRegion([-1,0,0],[0,1,0],[0,0,-1]);
    let m5 = generateRegion([0,0, 1],[1,0,0],[0, 1,0]);
    let m6 = generateRegion([0,0,-1],[1,0,0],[0,-1,0]);
    m1.name = 'nz';
    m2.name = 'pz';
    m3.name = 'px';
    m4.name = 'nx';
    m5.name = 'py';
    m6.name = 'ny';

    group.add(m1);
    group.add(m2);
    group.add(m3);
    group.add(m4);
    group.add(m5);
    group.add(m6);

    if (projectMode.LiteMode.isActive()){
        textureLoader.load(imgUrl2+'/nz'+format2, t => {
            m1.material.uniforms.map1.value = t;
            m1.material.uniforms.map2.value = t;

        });
        textureLoader.load(imgUrl2+'/pz'+format2, t => {
            m2.material.uniforms.map1.value = t;
            m2.material.uniforms.map2.value = t;
        });
        textureLoader.load(imgUrl2+'/px'+format2, t => {
            m3.material.uniforms.map1.value = t;
            m3.material.uniforms.map2.value = t;

        });
        textureLoader.load(imgUrl2+'/nx'+format2, t => {
            m4.material.uniforms.map1.value = t;
            m4.material.uniforms.map2.value = t;
        });
        textureLoader.load(imgUrl2+'/py'+format2, t => {
            m5.material.uniforms.map1.value = t;
            m5.material.uniforms.map2.value = t;
        });
        textureLoader.load(imgUrl2+'/ny'+format2, t => {
            m6.material.uniforms.map1.value = t;
            m6.material.uniforms.map2.value = t;
        });


    } else {
        textureLoader.load(imgUrl1+'/nz'+format, t=>m1.material.uniforms.map1.value=t);
        textureLoader.load(imgUrl1+'/pz'+format, t=>m2.material.uniforms.map1.value=t);
        textureLoader.load(imgUrl1+'/px'+format, t=>m3.material.uniforms.map1.value=t);
        textureLoader.load(imgUrl1+'/nx'+format, t=>m4.material.uniforms.map1.value=t);
        textureLoader.load(imgUrl1+'/py'+format, t=>m5.material.uniforms.map1.value=t);
        textureLoader.load(imgUrl1+'/ny'+format, t=>m6.material.uniforms.map1.value=t);

        if (cylinderBg && cylinderBg === 'sky') {
            textureLoader.load(imgUrl2+'/nz'+format2, t=>m1.material.uniforms.map2.value=t);
            textureLoader.load(imgUrl2+'/pz'+format2, t=>m2.material.uniforms.map2.value=t);
            textureLoader.load(imgUrl2+'/px'+format2, t=>m3.material.uniforms.map2.value=t);
            textureLoader.load(imgUrl2+'/nx'+format2, t=>m4.material.uniforms.map2.value=t);
            textureLoader.load(imgUrl2+'/py'+format2, t=>m5.material.uniforms.map2.value=t);
            textureLoader.load(imgUrl2+'/ny'+format2, t=>m6.material.uniforms.map2.value=t);
        }
    }


    const {position,rotation,scale} = config.resources.background;
    group.position.set(position.x,position.y,position.z);
    group.rotation.set (rotation.x,rotation.y,rotation.z);
    group.scale.set(scale.x,scale.y,scale.z);
    threeJsVariables.scene.add(group);

    if (cylinderBg && cylinderBg === 'sky')
        threeJsVariables.background = group;
};

const loadBundle = (manager) => {

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('js/libs/gltf/');

    const ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath( 'js/libs/basis/' ).detectSupport(threeJsVariables.renderer);

    const loader = new GLTFLoader(manager);
    loader.setDRACOLoader(dracoLoader);
    loader.setKTX2Loader(ktx2Loader);
    loader.setMeshoptDecoder( MeshoptDecoder );

    const textureLoader = new TextureLoader(manager);

    projectMode.LiteMode = new LiteMode();
    projectMode.LiteMode.init(textureLoader, manager);
    // projectMode.LiteMode.loadBuildingModel(manager);
    prepapeSphere(textureLoader);
    // projectMode.LiteMode.addToScene();

    if (projectMode.LiteMode.isActive() && !projectMode.LiteMode.modeIs('regular')) return;

    // загружаем текстуры для зданий

    let buildingsMaterial;

    const materialConfig = config.resources.bundle.material;
    if (materialConfig.specularMap) {
        buildingsMaterial = new MeshPhongMaterial();
    } else {
        buildingsMaterial = new MeshStandardMaterial();
    }
    const url = `${config.cdnRepository}/${config.projectFolder}/resources/textures/building/`;
    for (let key in materialConfig) {
        const value = materialConfig[key];
        if ( value instanceof Object){
            const t = textureLoader.load(url + value.path);
            t.name = key;
            if (key==='envMap') {
                t.mapping = EquirectangularReflectionMapping
            }

            Object.entries(value).forEach(([key,value]) => {
                if (key !== 'path') {
                    t[key]  = value;
                    t.encoding = LinearEncoding;
                    t.wrapS = RepeatWrapping;
                    t.wrapT = RepeatWrapping;
                    t.offset.set(0,0);
                    t.repeat.set(1,1);
                }
            });

            globalTextures[url + value.path] = t;

            buildingsMaterial[key] = t
        } else {
            buildingsMaterial[key] = value
        }
    }

    // Add multiple materials
    if (config.resources.bundle.materials?.length) {
        const materialsArray = config.resources.bundle.materials;
        const url = `${config.cdnRepository}/${config.projectFolder}/resources/textures/building/`;

        materialsArray.forEach(material => {

            multiTextures[material.building] = {};
            const textures = material.textures;

            // Load textures;
            Object.entries(textures).forEach(([key,value]) =>{
                const fullPath = url + value.path;
                let texture;

                if (globalTextures.hasOwnProperty(fullPath)) {
                    texture = globalTextures[fullPath];
                } else {
                    texture = textureLoader.load(url + value.path);
                    texture.name = key;
                    texture.encoding = LinearEncoding;
                    texture.wrapS = RepeatWrapping;
                    texture.wrapT = RepeatWrapping;
                    texture.offset.set(0,0);
                    texture.repeat.set(1,1);

                    if (key==='envMap'){
                        texture.mapping = EquirectangularReflectionMapping;
                    }

                    globalTextures[url + value.path] = texture;
                }

                multiTextures[material.building][key] = texture;
            });
        });
    }

    addSceneObjects(manager,textureLoader,loader);


    // config.cdnRepository+'/'+config.projectFolder+'/resources/building/bundle.glb',
    loader.load(config.cdnRepository+'/'+config.projectFolder+'/resources/building/bundle.glb',(gltf)=>{
        //
        const {folder,name,format,count} = config.resources.buildings;
        const {projectRotation} = config.threejs;

        const projectGroup = new Group();
        projectGroup.name = 'Project Buildings';
        projectGroup.rotation.set(projectRotation.x,projectRotation.y,projectRotation.z);

        for (let i = 0; i < count; i++){
            const building_name = name + String(i+1).padStart(2, '0');
            const building = gltf.scene.getObjectByName(building_name);
            let group = bundle_initAutoBuildingsScript(projectGroup,building,[]);

            const material = buildingsMaterial.clone();

            if (Object.keys(multiTextures).length && multiTextures.hasOwnProperty(building_name)){
                const additionalTextures = multiTextures[building_name];

                Object.entries(additionalTextures).forEach(([key,value]) =>{
                   material[key] = value;
                });
            }


            group.traverse(o=>{
                if (o.isMesh) {
                    if (o.material.map) {
                        /*
                        for (let key in materialConfig) {
                            if (key==='envMap' || key==='lightMap'){
                                continue
                            }

                            const config = materialConfig[key]
                            if ( config instanceof Object){
                                let texture = material[key]
                                texture.encoding = LinearEncoding
                                texture.matrix.copy(o.material.map.matrix)
                                texture.matrixAutoUpdate = o.material.map.matrixAutoUpdate

                                texture.offset.copy(o.material.map.offset)
                                texture.repeat.copy(o.material.map.repeat)
                                texture.center.copy(o.material.map.center)
                                texture.rotation = o.material.map.rotation
                            }
                        }
                        */
                        o.material = material
                    }
                }
            })
        }

        const crmData = getCrmData();
        crmData.then(response => {

            const sBoxes = [];
            response[0].data.buildings.forEach((building,index) =>{
                sBoxes.push(gltf.scene.getObjectByName(building.buildingEngineId+'_selectionBoxes'))
            });

            bundle_initAutoHighlightsScript(sBoxes.concat([response]));
            const poiData = getPoiData();
            poiData.then(response => {
                config.poiData = response[0].data;
                config.poiCategories = response[1].data;
            });

            addObjectsToOpacity();
            flyCameraAnimation();

        });

        //
        threeJsVariables.scene.add(projectGroup);
        threeJsVariables.renderer.render(threeJsVariables.scene, threeJsVariables.perspectiveCamera);
        //
        // addSceneObjects(manager,textureLoader,loader);

        if (sceneObjectsGroup.children.length) {
            threeJsVariables.scene.add(sceneObjectsGroup);
        }

        // projectMode.LiteMode.addToScene();
    },undefined,(err)=>{
        settingsData.loadingError = true;
    });


    // if (projectMode.LiteMode.isActive()) return;

    if (config.resources.bundle.minitiles && (isMobile||isTablet)){
        const tiles = new Group()
        tiles.name = 'async env'
        let tileConfig = config.resources.bundle.tilesConfig
        tiles.scale.set(tileConfig.scale[0],tileConfig.scale[1],tileConfig.scale[2])
        tiles.position.set(tileConfig.position[0],tileConfig.position[1],tileConfig.position[2])
        tiles.rotation.set(tileConfig.rotation[0],tileConfig.rotation[1],tileConfig.rotation[2])
        threeJsVariables.scene.add(tiles)

        let loaded = config.resources.bundle.tilesCount

        const textureLoader2 = new TextureLoader()

        for (let i=0;i<config.resources.bundle.tilesCount;i++){
            loader.load(config.cdnRepository+'/'+config.projectFolder+config.resources.bundle.tiles+'/tiles'+i+'.glb',(gltf)=>{
                let g = gltf.scene.children[0]
                g.name = i
                let _m = g.material 
                _m.map.encoding = LinearEncoding
                g.material = basic_tile.clone()
                g.material.uniforms.map.value = _m.map
                g.material.uniforms.hmap.value = basic_tile.uniforms.hmap.value
                _m.map.updateMatrix()
                g.material.uniforms.uvTransform.value = _m.map.matrix
                tiles.add(g)

                loaded = loaded - 1
                if (loaded===0){
                    // загружаем текстуры для 3dtiles в хорошем качестве
                    for (let j=0;j<config.resources.bundle.tilesCount;j++){
                        let o = tiles.getObjectByName(j)
                        o.castShadow = false
                        /*
                        textureLoader2.load(config.resources.bundle.tiles+'3dtile_txt'+j+'.jpg',(t)=>{
                            let m = o.material
                            t.encoding = LinearEncoding
                            t.matrix.copy(m.map.matrix)
                            t.matrixAutoUpdate = m.map.matrixAutoUpdate
                            t.offset.copy(m.map.offset)
                            t.repeat.copy(m.map.repeat)
                            t.center.copy(m.map.center)
                            t.rotation = m.map.rotation
                            m.map = t
                        })
                        */
                    }
                }

            })
        }
    }else{
        environmentToggle()
    }

    if (!config.threejs.renderer?.logarithmicDepthBuffer){
        textureLoader.load(`${config.cdnRepository}/${config.projectFolder}/resources/textures/environment/hmap.png`,(t)=>{
            setHMap(t)
        },(err)=>{
            //console.log(err)
        },(p)=>{
            //console.log(p)
        })
    }

}

export function initProjectMainScript() {
    const manager = new THREE.LoadingManager();
    manager.onStart = function (url, itemsLoaded, itemsTotal) {
    };
    if (!config.resources.bundle){
        const dracoLoader = new DRACOLoader(manager);
        dracoLoader.setDecoderPath('../../../js/libs/gltf/');

        const textureLoader = new THREE.TextureLoader(manager);

        const textures = loadModelTexturesFromAzure(textureLoader);
        const models = loadBuildingModelsFromAzure(manager,dracoLoader);
        const crmData = getCrmData();

        crmData
            .then(response => {
                const selectionBoxes = loadHighlightsModelFromAzure(response,dracoLoader);
                return selectionBoxes;
            })
            .then(sBoxes => {
                const allForHighlights = lazyLoadingAll(sBoxes.concat(crmData));
                allForHighlights.then(response => {
                    initAutoHighlightsScript(response);
                    // const [buildingSettings] = response.filter(el => Array.isArray(el))[0];
                    const poiData = getPoiData();
                    poiData.then(response => {
                        // console.log(buildingSettings);
                        config.poiData = response[0].data;
                        config.poiCategories = response[1].data;
                        // response.push(buildingSettings);
                        // initPoiData(response);
                    });
                });

                const opacityObjects = lazyLoadingAll([allForBuildings, allForHighlights]);
                opacityObjects.then(() => {
                    addObjectsToOpacity();
                    // if (config.cesiumEnvironment.envType !== EnvironmentTypes.TilesEnv){
                        flyCameraAnimation();
                    // }
                    // flyToBuildings();
                });
            });

        const allForBuildings = lazyLoadingAll(textures.concat(models));
        allForBuildings.then(response => initAutoBuildingsScript(response));

        // const sceneBgTextures = sceneBackground(settingsData.low_performance_mode ? 'BUILDING_ONLY' : '');
        // sceneBgTextures.then(response => threeJsVariables.scene.background = response);

        // const sceneBgTextures2 = loadSixBoxImages();
        // sceneBgTextures2.then(response => sphereSceneBackground(response));
        prepapeSphere(textureLoader);
        // sphereBackground(textureLoader);
        environmentToggle();
        addSceneObjects(manager);
    }else{
        loadBundle(manager)
    }

    manager.onProgress = (url, itemsLoaded, itemsTotal) => {
        // console.log(`itemsTotal / itemsLoaded = ${itemsTotal} / ${itemsLoaded}`);
        settingsData.totalPercent = (itemsLoaded / itemsTotal * 100);
    };


    manager.onLoad = async () => {
        //await loadCylinderGrid();
        // loadCylinderGrid();
        // if (config.cesiumEnvironment.envType !== EnvironmentTypes.TilesEnv){
        //removePreloader();
        await startProjectWithError();
        projectMode.Cylinder = new Cylinder();
        projectMode.FloorPlan = new FloorPlan();

        // }
    };
}


async function startProjectWithError () {
    // Check error
    if (!settingsData.loadingError) return;

    console.log('%c Project has been started with error', 'color: #ff0000');

    const crmData = await getCrmData();
    const [crmSettings, crmProps/*, dictionary*/] = crmData;
    setProjectSettings(crmSettings/*, dictionary*/);

    const poiData = await getPoiData();
    config.poiData = poiData[0].data;
    config.poiCategories = poiData[1].data;

    flyCameraAnimation();
}

/*export function initProjectMainScript () {
  const manager = new THREE.LoadingManager();
  manager.onStart = function (url, itemsLoaded, itemsTotal) {};

  const textureLoader = new THREE.TextureLoader(manager);

  const textures = loadModelTexturesFromAzure(textureLoader);
  const models = loadBuildingModelsFromAzure(manager);
  const crmData = getCrmData();
  const poiData = getPoiData();
  poiData.then(response => initPoiData(response));
  const selectionBoxes = loadHighlightsModelFromAzure(manager);
  const sceneBgTextures = sceneBackground(settingsData.low_performance_mode ? 'BUILDING_ONLY' : '');

    // Start buildings construct
  const allForBuildings = lazyLoadingAll(textures.concat(models));
  allForBuildings.then(response => initAutoBuildingsScript(response));

  //Start highlights construct
  const allForHighlights = lazyLoadingAll(selectionBoxes.concat(crmData));
  allForHighlights.then(response => initAutoHighlightsScript(response));

  // Add instanced mesh to objects_to_opacity
  const opacityObjects = lazyLoadingAll([allForBuildings,allForHighlights]);
  opacityObjects.then(()=> addObjectsToOpacity());

  // Start env load
  const startLoadTiles = lazyLoadingAll([sceneBgTextures,allForBuildings]);
  startLoadTiles.then(response =>{
        threeJsVariables.scene.background = response[0];
        flyToBuildings();
        if (!settingsData.low_performance_mode) loadEnvironment();
  });

  manager.onLoad = function () {};

  manager.onProgress = function (url, itemsLoaded, itemsTotal) {
      console.log('url: ', url);
      console.log('itemsLoaded: ', itemsLoaded);
      console.log('itemsTotal: ', itemsTotal);
    settingsData.totalPercent = (itemsLoaded / itemsTotal * 100);

    if (settingsData.totalPercent === 100) {
        removePreloader();
    }
  };
}*/

function flyToBuildings() {
    // setTimeout(() => {
    //     //     let defaultData = settingsData.globalSettings.animations.defaultForeshortening;
    //     //     const { minDistance } = getOrbitControlsDistanceAndPan(ViewMode.Orbit);
    //     //
    //     //     const position = {
    //     //         x: defaultData.position.x,
    //     //         y: defaultData.position.y,
    //     //         z: defaultData.position.z - 250
    //     //     };
    //     //
    //     //
    //     //   animateCamera(
    //     //     threeJsVariables.perspectiveCamera.position,
    //     //     position,
    //     //     threeJsVariables.orbitControls.target,
    //     //     threeJsVariables.orbitControls.target,
    //     //     getCurrentCameraDistance(),
    //     //     minDistance,
    //     //     undefined,
    //     //     undefined,
    //     //     5000,
    //     //     TWEEN.Easing.Linear.None,
    //     //     ()=>{},
    //     //     ()=>{
    //     //
    //     //         const {cameraVector} = getWorldData();
    //     //
    //     //         animateCamera(
    //     //           cameraVector,
    //     //           defaultData.position,
    //     //           threeJsVariables.orbitControls.target,
    //     //           threeJsVariables.orbitControls.target,
    //     //           getCurrentCameraDistance(),
    //     //           minDistance,
    //     //           undefined,
    //     //           undefined,
    //     //           9000,
    //     //           TWEEN.Easing.Linear.None,
    //     //           ()=>{},
    //     //           ()=>{
    //     //               setMinAndMaxControlsDistance(ViewMode.Orbit);
    //     //           }
    //     //         );
    //     //   });
    //     //
    //     // }, 700);
}
